import { format } from 'date-fns';
import { MessageCircleQuestion } from 'lucide-react';

import { Tooltip } from '@/components/ui/tooltip';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/platform-status/components/cards';
import type { PlatformComponent } from '@/platform-status/entities/platform-component';
import { label } from '@/utils/format/label';
import { formatPercentage } from '@/utils/format/number';

import { PlatformComponentDay } from './platform-component-day';
import { PlatformComponentStatusBadge } from './platform-component-status-badge';
import { PlatformComponentStatusIcon } from './platform-component-status-icon';

export type PlatformComponentItemProps = {
  platformComponent: PlatformComponent;
};

const dateKey = (date: Date) => format(date, 'yyyy-MM-dd');

export function PlatformComponentItem({
  platformComponent,
}: PlatformComponentItemProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex flex-row items-center justify-start gap-2">
          <span>{platformComponent.name}</span>
          {platformComponent.description ? (
            <Tooltip
              content={platformComponent.description}
              className="max-w-sm"
            >
              <MessageCircleQuestion className="size-4" />
            </Tooltip>
          ) : null}
        </CardTitle>
        <div className="flex flex-row items-center gap-2">
          <PlatformComponentStatusIcon
            activeErrorClass={platformComponent.activeErrorClass}
          />
          <PlatformComponentStatusBadge
            activeErrorClass={platformComponent.activeErrorClass}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid size-14 w-full auto-cols-fr grid-flow-col gap-px">
          {platformComponent.days.map((day) => (
            <PlatformComponentDay key={dateKey(day.date)} day={day} />
          ))}
        </div>
        <div className="flex justify-between text-sm text-gray-500">
          <span>
            {platformComponent.days.length}{' '}
            {label(['day', 'days'], platformComponent.days.length)} ago
          </span>
          <span className="font-medium text-gray-900">
            {formatPercentage(platformComponent.uptime, 2)} uptime
          </span>
          <span>Today</span>
        </div>
      </CardContent>
    </Card>
  );
}
